import React, { useMemo, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import AddItemModal from '../fields/ListBuilder/AddItemModal';
import { ListBuilderItem } from '../fields/ListBuilder';
import FacilityModal from '../FacilityModal';
import {
  useFacilitiesPage,
  useUpdateFacility,
} from '../../../services/api/domains/facilities/hooks';
import { Facility } from '../../../services/api/domains/facilities/types';
import { FacilitySummary } from '../../../services/api/domains/manufacturers/types';
import LgmButton from '../../LgmButton';
import {
  GRAY_200,
  GRAY_400,
  GRAY_500,
  GRAY_700,
  GRAY_900,
} from 'theme/grayScale';
import { COLOR_DANGER } from 'theme/colors';

const MISSING = '—';

const CELL_STYLE: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '13px',
  color: GRAY_900,
};

interface FacilitiesPanelProps {
  facilities: FacilitySummary[];
  onChange: (facilities: FacilitySummary[]) => void;
  isEditMode: boolean;
}

/**
 * Manufacturer modal Facilities panel (issue #653, Task 10, Sub-task 10.5).
 * Matches today's facilities view (Facility Name/City/State/Country —
 * Open Question 3a) as a read-only table — no inline field editing (Open
 * Question 3b). Add/remove are staged locally and deferred to the modal's
 * Save button, same as every other `ModalFieldRenderer`-driven field
 * (`facility_ids` already flows through `updateFacilitiesForManufacturer`).
 * The existing Edit button still opens the real `FacilityModal`, which
 * saves immediately via `useUpdateFacility` — a separate, already-existing
 * write path this panel doesn't change.
 */
const FacilitiesPanel: React.FC<FacilitiesPanelProps> = ({
  facilities,
  onChange,
  isEditMode,
}) => {
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const facilitiesPage = useFacilitiesPage();
  const updateFacilityMutation = useUpdateFacility();
  const allFacilities = facilitiesPage.data;

  const availableItems = useMemo<ListBuilderItem[]>(() => {
    const linkedIds = new Set(facilities.map(f => f.facility_id));
    return allFacilities
      .filter(f => !linkedIds.has(f.facility_id))
      .map(f => ({ id: f.facility_id, label: f.facility_name }));
  }, [allFacilities, facilities]);

  const handleAddFacilities = (selectedIds: number[]) => {
    const added: FacilitySummary[] = selectedIds
      .map(id => allFacilities.find(f => f.facility_id === id))
      .filter((f): f is Facility => f !== undefined)
      .map(f => ({
        facility_id: f.facility_id,
        facility_name: f.facility_name,
        city: f.city,
        state_province: f.state_province,
        country_code: f.country_code,
        comments: f.comments,
      }));
    onChange([...facilities, ...added]);
    setIsAddModalOpen(false);
  };

  const handleRemove = (facilityId: number) => {
    onChange(facilities.filter(f => f.facility_id !== facilityId));
  };

  const handleEditClick = (facilityId: number) => {
    const fullFacility = allFacilities.find(f => f.facility_id === facilityId);
    if (fullFacility) {
      setEditingFacility(fullFacility);
    }
  };

  const handleSaveFacility = async (facilityData: Facility) => {
    try {
      await updateFacilityMutation.mutate(facilityData);
      setEditingFacility(null);
      // Reflect the (possibly renamed) facility in this manufacturer's own
      // staged list without waiting for a full modal reopen.
      onChange(
        facilities.map(f =>
          f.facility_id === facilityData.facility_id
            ? {
                ...f,
                facility_name: facilityData.facility_name,
                city: facilityData.city,
                state_province: facilityData.state_province,
                country_code: facilityData.country_code,
                comments: facilityData.comments,
              }
            : f
        )
      );
    } catch (err) {
      console.error('❌ Save facility failed:', err);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}>
        <span style={{ fontSize: '14px', fontWeight: 500, color: GRAY_700 }}>
          Facilities
        </span>
        {isEditMode && (
          <LgmButton
            variant="flat"
            onClick={() => setIsAddModalOpen(true)}
            style={{ fontSize: '13px', padding: '4px 10px' }}>
            + Add Facility
          </LgmButton>
        )}
      </div>

      {facilities.length === 0 ? (
        <div
          style={{
            padding: '16px',
            textAlign: 'center',
            color: GRAY_400,
            fontSize: '13px',
            border: `1px dashed ${GRAY_200}`,
            borderRadius: '6px',
          }}>
          No facilities associated with this manufacturer
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${GRAY_200}` }}>
              {['Facility Name', 'City', 'State/Province', 'Country', ''].map(
                header => (
                  <th
                    key={header}
                    style={{
                      textAlign: 'left',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      color: GRAY_500,
                      padding: '6px 8px',
                    }}>
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {facilities.map(facility => (
              <tr
                key={facility.facility_id}
                style={{ borderBottom: `1px solid ${GRAY_200}` }}>
                <td style={CELL_STYLE}>{facility.facility_name}</td>
                <td style={CELL_STYLE}>{facility.city || MISSING}</td>
                <td style={CELL_STYLE}>{facility.state_province || MISSING}</td>
                <td style={CELL_STYLE}>{facility.country_code || MISSING}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  <button
                    type="button"
                    aria-label={`Edit ${facility.facility_name}`}
                    onClick={() => handleEditClick(facility.facility_id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: GRAY_500,
                      padding: '4px',
                    }}>
                    <Pencil size={14} />
                  </button>
                  {isEditMode && (
                    <button
                      type="button"
                      aria-label={`Remove ${facility.facility_name}`}
                      onClick={() => handleRemove(facility.facility_id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: COLOR_DANGER,
                        padding: '4px',
                      }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isAddModalOpen && (
        <AddItemModal
          title="Add Facilities"
          availableItems={availableItems}
          onConfirm={handleAddFacilities}
          onCancel={() => setIsAddModalOpen(false)}
        />
      )}

      {editingFacility && (
        <FacilityModal
          facility={editingFacility}
          initialMode="EDIT"
          save={handleSaveFacility}
          close={() => setEditingFacility(null)}
        />
      )}
    </div>
  );
};

export default FacilitiesPanel;
